if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.restoreQuickAddDefaults();
        if (!this.requiresSizeSelection || this.dataset.sizeSelectionPending === 'false') {
          this.variantIdInput.disabled = false;
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.requiresSizeSelection && !this.canSubmitSizeSelection()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        let uncertainAdd = false;
        let confirmedAddNeedsRefresh = false;

        const addToCart = () => {
          // A preceding cart change can remove the source or satisfy the match
          // while this drawer form waits for the shared writer lane.
          if (this.closest('cart-drawer') && !this.isConnected) return Promise.resolve();
          if (this.requiresSizeSelection &&
              (!this.canSubmitSizeSelection() || formData.get('id') !== this.variantIdInput.value)) {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            return Promise.resolve();
          }
          uncertainAdd = true;
          return fetch(`${routes.cart_add_url}`, config)
            .then(async (response) => {
              let responseBody;

              try {
                responseBody = await response.json();
              } catch (error) {
                throw new Error('The cart returned an invalid response.');
              }

              if (!response.ok && !responseBody.status) {
                throw new Error('The cart returned an unexpected response.');
              }

              return responseBody;
            })
            .then(async (response) => {
              if (response.status) {
                uncertainAdd = false;
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }

              uncertainAdd = false;
              confirmedAddNeedsRefresh = true;
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                await new Promise((resolve) => {
                  document.body.addEventListener('modalClosed', resolve, { once: true });
                  quickAddModal.hide(true);
                });
              }
              await this.cart.renderContents(response);
              confirmedAddNeedsRefresh = false;
              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              await publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
                cartSurface: this.cart.tagName === 'CART-DRAWER' ? 'drawer' : 'notification',
              });
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            })
            .catch(async (e) => {
              console.error(e);
              const errorMessage = window.cartStrings?.error || 'There was an error while updating your cart. Please try again.';
              this.handleErrorMessage(errorMessage);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: errorMessage,
                message: errorMessage,
              });
              this.error = false;
              if (this.cart?.tagName === 'CART-DRAWER') {
                this.error = confirmedAddNeedsRefresh;
                if (uncertainAdd) {
                  try {
                    // A lost response might still have added the product. Re-read
                    // before allowing another add; never retry the POST silently.
                    await this.cart.renderContents({ sections: {} });
                    const errors = this.cart.querySelector('#CartDrawer-CartErrors');
                    if (errors) errors.textContent = errorMessage;
                  } catch (refreshError) {
                    console.error(refreshError);
                    this.error = true;
                  }
                }
                if (this.error) {
                  // Confirmed adds already exhausted renderContents' one recovery
                  // read. Keep the cart link instead of repeating that request.
                  this.cartUncertain = true;
                  const cartLink = this.querySelector('[data-cart-recovery-link]');
                  if (cartLink) cartLink.hidden = false;
                }
              }
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');

              CartPerformance.measureFromEvent("add:user-action", evt);
            });
        };
        return window.CartMutations ? window.CartMutations.run(addToCart) : addToCart();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // Quick-add clones MainProduct HTML. Restore its existing implicit defaults
      // before removing the main-form-only gate; recommendations never opt in.
      restoreQuickAddDefaults() {
        const productInfo = this.closest('product-info');
        if (!this.requiresSizeSelection || !productInfo?.dataset.originalSection) return;
        const picker = productInfo.querySelector('variant-selects');
        const defaultVariant = picker.querySelector('[data-quick-add-variant]');
        const variant = defaultVariant ? JSON.parse(defaultVariant.textContent) : productInfo.getSelectedVariant(productInfo);
        picker.querySelectorAll('.product-form__input').forEach((group, index) => {
          group.querySelectorAll('input[type="radio"], option').forEach((input) => {
            const selected = input.value === variant?.options[index];
            if (input.tagName === 'OPTION') input.selected = selected;
            else input.checked = selected;
            input.toggleAttribute(input.tagName === 'OPTION' ? 'selected' : 'checked', selected);
          });
        });
        picker.querySelectorAll('option:not([data-option-value-id])').forEach((option) => option.remove());
        picker.selectedInputs.forEach((input) => {
          if (input) picker.updateSelectionMetadata({ target: input.tagName === 'OPTION' ? input.parentElement : input });
        });
        delete picker.dataset.requireSizeSelection;
        delete this.dataset.requireSizeSelection;
        delete this.dataset.sizeSelectionPending;
        picker.querySelector('[data-selected-variant]').textContent = JSON.stringify(variant);
        this.variantIdInput.value = variant?.id ?? '';
        this.submitButton.disabled = this.submitButton.dataset.defaultSubmitDisabled === 'true';
        this.submitButtonText.textContent = this.submitButtonText.dataset.defaultSubmitText;
      }

      get requiresSizeSelection() {
        return this.dataset?.requireSizeSelection !== undefined;
      }

      canSubmitSizeSelection() {
        if (this.dataset.sizeSelectionPending !== 'false' || this.submitButton.disabled ||
            this.variantIdInput.disabled || !this.variantIdInput.value) return false;
        const productInfo = this.closest('product-info');
        const variant = productInfo?.getSelectedVariant(productInfo);
        return Boolean(variant?.available && String(variant.id) === this.variantIdInput.value &&
          productInfo.variantSelectors?.matchesVariant(variant));
      }

      setSizeSelectionPending(pending) {
        if (!this.requiresSizeSelection) return;
        this.dataset.sizeSelectionPending = String(pending);
        if (pending) {
          this.variantIdInput.value = '';
          this.variantIdInput.disabled = true;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (this.requiresSizeSelection && this.dataset.sizeSelectionPending === 'true') {
          disable = true;
          text = this.dataset.productSoldOut === 'true' ? window.variantStrings.soldOut : window.variantStrings.selectSize;
        }
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
